import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ChartBindingValue,
  CustomChartBreakdownItem,
  CustomChartValueType,
  IndicatorSelectHandler,
} from '@/entities/dashboards';
import { cn, preventShiftSelection, type QualityZoneRanges } from '@/shared/lib';
import { EmptyState, RichTextContent } from '@/shared/ui';
import { StatBox } from '../../parts/StatBox';
import { DASHBOARD_CLICKABLE_BLOCK_CLASS, chartNumberDigits } from '../../utils';
import { chartElementBgClass, chartPointValueText, groupChartItems, type ChartAppearance } from '../chartElements';
import {
  type ChartDatePrecision,
  chartDatePrecision,
  isChartDateType,
  shownChartLabel,
  shownChartValue,
} from '../chartValues';
import { ChartCardShell } from '../ChartCardShell';
import { ChartGroupSection } from './ChartGroupSection';
import { ChartLegend } from './ChartLegend';
import { valueRuleLegendItems } from './chartLegendItems';

type Props = {
  title: string;
  items: CustomChartBreakdownItem[];
  /** Тип величины категории: по нему величина-дата показывается датой, а число — числом */
  valueType: CustomChartValueType;
  /** Пороги зон качества пользователя: ими окрашен блок со своим потолком */
  zoneRanges: QualityZoneRanges;
  /** Оформление диаграммы: цвета категорий и правила раскраски блоков по величине */
  appearance?: ChartAppearance;
  /** Переход к диалогам категории; не задан — блоки нажатий не принимают и нажимаемыми не показаны */
  onSelect?: IndicatorSelectHandler;
  /** Выбор категории наполняет связанную диаграмму; задан — выбор заменяет переход к диалогам */
  onSelectElement?: (value: ChartBindingValue) => void;
  /** Выбранная категория — она подсвечена, повторный выбор снимает её */
  selectedElement?: ChartBindingValue;
};

/**
 * Величина блока категории. Число со своим потолком и размерностью показано, как элемент
 * диаграммы со своей шкалой, — «42 / 100»; прочее — тем видом, каким его дала выборка.
 */
const breakdownValueText = (
  item: CustomChartBreakdownItem,
  valueType: CustomChartValueType,
  precision: ChartDatePrecision,
  digits: number,
): string => {
  if (typeof item.value !== 'number' || isChartDateType(valueType)) {
    return shownChartValue(item.value, valueType, precision, digits);
  }
  return chartPointValueText(
    { label: item.label, value: item.value, maxValue: item.maxValue, unit: item.unit },
    'scale',
    digits,
  );
};

/**
 * Показатель с переходом к выборке: категории показаны отдельными блоками с названием и величиной —
 * как «Зоны качества» и «Красные зоны» стандартного дашборда.
 *
 * Блок читается как отдельный показатель категории: под названием стоит его текст результата,
 * собранный сервером по строке категории, а величина со своим потолком показана со шкалой, и блок
 * тогда окрашен по зоне — по общему старшинству цветов элемента. Без текста блок показан названием
 * и величиной.
 *
 * Полос сравнения величин у этого типа нет: сравнение — дело столбчатой диаграммы, а этот тип
 * выбирают ради перехода к диалогам, и блок читается как то, что можно нажать. Блок, за которым
 * диалогов нет, разбирать нечего — он нажимаемым не показан.
 *
 * У диаграммы, выбор в которой наполняет связанную, выбор занят разбором: категория тогда не
 * уводит к списку диалогов, а выбирается на самом дашборде.
 *
 * Категории, отнесённые выборкой к группам, собраны в озаглавленные группы в порядке их первого
 * появления: одноимённые категории разных групп остаются разными.
 *
 * Число категорий заранее не известно, а доля ширины у диаграммы любая, поэтому блоки встают в
 * колонки по ширине самой диаграммы, а не по ширине экрана.
 */
export const SelectionBreakdownChartView: FC<Props> = ({
  title,
  items,
  valueType,
  zoneRanges,
  appearance,
  onSelect,
  onSelectElement,
  selectedElement,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const legendItems = useMemo(() => valueRuleLegendItems(appearance?.valueColorRules, t), [appearance, t]);

  // Точность времени берётся по величинам всех категорий сразу — соседние блоки написаны одинаково.
  const precision = chartDatePrecision(
    items.map((item) => item.value),
    valueType,
  );
  const digits = chartNumberDigits(items.map((item) => item.value));

  const groups = groupChartItems(items);

  return (
    <ChartCardShell title={title}>
      {items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="@container flex flex-col gap-4">
          {groups.map((group) => (
            <ChartGroupSection key={group.title} title={group.title}>
              <div className="grid grid-cols-1 gap-3 @md:grid-cols-2 @3xl:grid-cols-3">
                {group.items.map((item, index) => {
                  const isSelectable = !!onSelectElement;
                  const isClickable = isSelectable || (!!onSelect && item.conversationIds.length > 0);
                  const isDimmed = selectedElement !== undefined && selectedElement !== item.label;
                  // Цвет, заданный категории поимённо, старше правил величины, а они — зоны по
                  // своему потолку. Величина категории бывает и строкой — правила раскраски задают
                  // границы числа, и такую категорию красит только её цвет.
                  const colorClass = chartElementBgClass(
                    {
                      label: item.label,
                      value: typeof item.value === 'number' ? item.value : null,
                      maxValue: item.maxValue,
                    },
                    appearance,
                    zoneRanges,
                  );
                  const block = (
                    <StatBox
                      label={shownChartLabel(item.label)}
                      description={item.text && <RichTextContent html={item.text} />}
                      value={breakdownValueText(item, valueType, precision, digits)}
                      className={cn(
                        'h-full',
                        colorClass ?? 'bg-card',
                        isClickable && DASHBOARD_CLICKABLE_BLOCK_CLASS,
                        isDimmed && 'opacity-60',
                      )}
                    />
                  );

                  // Ключ по месту в перечне: подписи категорий в пределах группы могут совпасть.
                  if (!isClickable) return <div key={index}>{block}</div>;

                  return (
                    <div
                      key={index}
                      role="button"
                      tabIndex={-1}
                      aria-label={isSelectable ? t('presets.chart.selectElement') : t('presets.chart.openDialogs')}
                      onMouseDown={preventShiftSelection}
                      onClick={(event) =>
                        isSelectable
                          ? onSelectElement(item.label)
                          : onSelect?.(
                              {
                                label: shownChartLabel(item.label),
                                conversationIds: item.conversationIds,
                                sourceColumns: item.sourceColumns,
                              },
                              event.shiftKey,
                            )
                      }
                    >
                      {block}
                    </div>
                  );
                })}
              </div>
            </ChartGroupSection>
          ))}
          <ChartLegend items={legendItems} />
        </div>
      )}
    </ChartCardShell>
  );
};
